import { authService } from "../auth/services/authService";
import { getTelemetrySessionId, trackApiFailure } from "../telemetry/telemetry";
import { apiBaseUrl } from "./apiConfig";
import { ApiError, type RequestOptions } from "./apiTypes";

const CLIENT_NAME = "FroppioForms";

export const createApiService = (controllerPath: string) => {
  const request = async <T>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    const { skipAuth = false, headers, ...restOptions } = options;
    const requestHeaders = new Headers(headers);
    requestHeaders.set("Content-Type", "application/json");
    // Lets backend logs be joined with the browser telemetry session.
    requestHeaders.set("X-Client-Id", getTelemetrySessionId() ?? CLIENT_NAME);

    if (!skipAuth) {
      const token = await authService.getAccessToken();
      if (!token) throw new ApiError("Sign in to continue.", 401);
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }

    const path = `${controllerPath}/${endpoint}`.replace(/\/\/+/g, "/");
    const method = (restOptions.method ?? "GET").toUpperCase();
    const startedAt = performance.now();
    const elapsed = () => Math.round(performance.now() - startedAt);

    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}/${path}`, { ...restOptions, headers: requestHeaders });
    } catch (error) {
      // Cancellation (e.g. React Query aborting a stale query) is not a failure.
      if (!(error instanceof DOMException && error.name === "AbortError")) {
        trackApiFailure({ method, path: `/${path}`, failure: "network", durationMs: elapsed() });
      }
      throw error;
    }

    if (!response.ok) {
      trackApiFailure({
        method, path: `/${path}`, failure: "http", status: response.status,
        correlationId: response.headers.get("X-Correlation-Id") ?? undefined, durationMs: elapsed(),
      });
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorData: unknown;
      try {
        errorData = await response.json();
        if (errorData && typeof errorData === "object") {
          if ("error" in errorData && typeof errorData.error === "string") {
            errorMessage = errorData.error;
          } else if ("message" in errorData && typeof errorData.message === "string") {
            errorMessage = errorData.message;
          }
        }
      } catch {
        // A non-JSON error still surfaces its HTTP status.
      }
      throw new ApiError(errorMessage, response.status, errorData);
    }

    return response.json();
  };

  return {
    get: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "GET" }),
    post: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "POST", body: JSON.stringify(data) }),
    put: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "PUT", body: JSON.stringify(data) }),
    patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "PATCH", body: JSON.stringify(data) }),
    delete: <T>(endpoint: string, options?: RequestOptions): Promise<T> =>
      request<T>(endpoint, { ...options, method: "DELETE" }),
  };
};

export type ApiServiceType = ReturnType<typeof createApiService>;
